import axios from "axios";
const isDev = process.env.NODE_ENV === "development";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = isDev ? "/api" : `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// ================= GLOBAL JWT INTERCEPTOR =================
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ================= GLOBAL 401 HANDLER =================
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn("Session expired. Redirecting to login...");
      localStorage.clear();
      window.location.href = "/hr/login";
    }
    return Promise.reject(error.response?.data || error);
  },
);

/* 
========================================================
⚠️ IMPORTANT
All original APIs remain unchanged below.
Interceptor automatically injects token,
so manual headers still work but are no longer required.
========================================================
*/

// Jobs API
export const jobsAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get("/jobs", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching jobs:", error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/jobs/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching job ${id}:`, error);
      throw error;
    }
  },

  create: async (jobData) => {
    try {
      const response = await api.post("/jobs", jobData);
      return response.data;
    } catch (error) {
      console.error("Error creating job:", error);
      throw error;
    }
  },

  apply: async (id) => {
    try {
      const response = await api.post(`/jobs/${id}/apply`);
      return response.data;
    } catch (error) {
      console.error(`Error applying to job ${id}:`, error);
      throw error;
    }
  },
};

// Companies API
export const companiesAPI = {
  getAll: async () => {
    try {
      const response = await api.get("/companies");
      return response.data;
    } catch (error) {
      console.error("Error fetching companies:", error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/companies/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching company ${id}:`, error);
      throw error;
    }
  },
};

// Categories API
export const categoriesAPI = {
  getAll: async () => {
    try {
      const response = await api.get("/categories");
      return response.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
  },
};

// Locations API
export const locationsAPI = {
  getAll: async () => {
    try {
      const response = await api.get("/locations");
      return response.data;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  },
};

// Profile API
export const profileAPI = {
  create: async (profileData) => {
    try {
      const response = await api.post("/profile/create", profileData);
      return response.data;
    } catch (error) {
      console.error("Error creating profile:", error);
      throw error;
    }
  },

  get: async (email) => {
    try {
      const response = await api.get(`/profile/${email}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching profile for ${email}:`, error);
      throw error;
    }
  },

  update: async (email, profileData) => {
    try {
      const response = await api.put(`/profile/${email}`, profileData);
      return response.data;
    } catch (error) {
      console.error(`Error updating profile for ${email}:`, error);
      throw error;
    }
  },

  uploadResume: async (email, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(`/profile/${email}/resume`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading resume for ${email}:`, error);
      throw error;
    }
  },

  uploadPhoto: async (email, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(`/profile/${email}/photo`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading photo for ${email}:`, error);
      throw error;
    }
  },

  delete: async (email) => {
    try {
      const response = await api.delete(`/profile/${email}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting profile for ${email}:`, error);
      throw error;
    }
  },
};

// Auth API
export const authAPI = {
  register: async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      return response.data;
    } catch (error) {
      console.error("Error during registration:", error);
      throw error;
    }
  },

  login: async (credentials) => {
    try {
      const response = await api.post("/auth/login", credentials);
      return response.data;
    } catch (error) {
      console.error("Error during login:", error);
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }

      const response = await api.get("/auth/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error getting current user:", error);
      throw error;
    }
  },

  logout: async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        await api.post(
          "/auth/logout",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      }
      localStorage.removeItem("authToken");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userFullName");
      localStorage.removeItem("userRole");
      localStorage.removeItem("companyId");
      return { success: true };
    } catch (error) {
      console.error("Error during logout:", error);

      localStorage.removeItem("authToken");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userFullName");
      localStorage.removeItem("userRole");
      localStorage.removeItem("companyId");

      throw error;
    }
  },
};

// Location API (ADD THIS BELOW)
export const locationAPI = {
  getAll: async () => {
    try {
      const response = await api.get("/locations");
      return response.data;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  },
};

// Admin API (Super Admin only)
export const adminAPI = {
  createCompany: async (companyData) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.post("/admin/create-company", companyData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating company:", error);
      throw error;
    }
  },

  createRecruiter: async (recruiterData) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.post(
        "/admin/create-recruiter",
        recruiterData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating recruiter:", error);
      throw error;
    }
  },

  getCompanies: async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get("/admin/companies", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching companies:", error);
      throw error;
    }
  },

  getCompanyById: async (companyId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get(`/admin/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching company:", error);
      throw error;
    }
  },

  updateCompany: async (companyId, companyData) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.put(
        `/admin/company/${companyId}`,
        companyData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating company:", error);
      throw error;
    }
  },

  deleteCompany: async (companyId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.delete(`/admin/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting company:", error);
      throw error;
    }
  },

  uploadCompanyLogo: async (companyId, file) => {
    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(
        `/admin/company/${companyId}/logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading company logo:", error);
      throw error;
    }
  },

  deleteCompanyLogo: async (companyId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.delete(`/admin/company/${companyId}/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting company logo:", error);
      throw error;
    }
  },

  getRecruiters: async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get("/admin/recruiters", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching recruiters:", error);
      throw error;
    }
  },

  getRecruiterById: async (recruiterId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get(`/admin/recruiter/${recruiterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching recruiter:", error);
      throw error;
    }
  },

  updateRecruiter: async (recruiterId, recruiterData) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.put(
        `/admin/recruiter/${recruiterId}`,
        recruiterData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating recruiter:", error);
      throw error;
    }
  },

  toggleRecruiterStatus: async (recruiterId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.put(
        `/admin/recruiter/${recruiterId}/toggle-status`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error toggling recruiter status:", error);
      throw error;
    }
  },

  deleteRecruiter: async (recruiterId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.delete(`/admin/recruiter/${recruiterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting recruiter:", error);
      throw error;
    }
  },

  getJobSeekers: async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get("/admin/job-seekers", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching job seekers:", error);
      throw error;
    }
  },

  getJobSeekerDetails: async (seekerId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get(`/admin/job-seeker/${seekerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching job seeker details:", error);
      throw error;
    }
  },

  getStats: async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get("/admin/stats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching admin stats:", error);
      throw error;
    }
  },

  bulkUploadJobSeekers: async (file) => {
    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(
        "/admin/bulk-upload-job-seekers",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error bulk uploading job seekers:", error);
      throw error;
    }
  },

  bulkUploadJobs: async (file) => {
    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/admin/bulk-upload-jobs", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error bulk uploading jobs:", error);
      throw error;
    }
  },

  downloadTemplate: async (type) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get(`/admin/download-template/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error downloading template:", error);
      throw error;
    }
  },

  // Notification APIs
  getNotifications: async (limit = 20, skip = 0) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.get(
        `/admin/notifications?limit=${limit}&skip=${skip}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  },

  markNotificationRead: async (notifId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.put(
        `/admin/notifications/${notifId}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error marking notification read:", error);
      throw error;
    }
  },

  markAllNotificationsRead: async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.put(
        "/admin/notifications/mark-all-read",
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error marking all notifications read:", error);
      throw error;
    }
  },

  // Withdraw/Reassign candidate APIs
  withdrawCandidate: async (applicationId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.put(
        `/admin/withdraw-candidate/${applicationId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error withdrawing candidate:", error);
      throw error;
    }
  },

  reassignCandidate: async (applicationId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await api.put(
        `/admin/reassign-candidate/${applicationId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error reassigning candidate:", error);
      throw error;
    }
  },

  // Download logs API
  getDownloadLogs: async (params = {}) => {
    try {
      const token = localStorage.getItem("authToken");
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append("limit", params.limit);
      if (params.skip) queryParams.append("skip", params.skip);
      if (params.recruiter_email)
        queryParams.append("recruiter_email", params.recruiter_email);
      if (params.date_from) queryParams.append("date_from", params.date_from);
      if (params.date_to) queryParams.append("date_to", params.date_to);

      const response = await api.get(
        `/admin/download-logs?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching download logs:", error);
      throw error;
    }
  },
};

// ===============================
// AUTH HEADER
// ===============================
const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

// ===============================
// RECRUITER API
// ===============================
export const recruiterAPI = {
  // ---------- JOB MANAGEMENT ----------

  postJob: async (jobData) =>
    (
      await api.post("/recruiter/post-job", jobData, {
        headers: getAuthHeader(),
      })
    ).data,

  getCompanies: async () =>
    (await api.get("/recruiter/companies", { headers: getAuthHeader() })).data,

  getMyJobs: async () =>
    (await api.get("/recruiter/my-jobs", { headers: getAuthHeader() })).data,

  updateJob: async (jobId, jobData) =>
    (
      await api.put(`/recruiter/job/${jobId}`, jobData, {
        headers: getAuthHeader(),
      })
    ).data,

  deleteJob: async (jobId) =>
    (await api.delete(`/recruiter/job/${jobId}`, { headers: getAuthHeader() }))
      .data,

  getAllLiveJobs: async () =>
    (await api.get("/recruiter/all-live-jobs", { headers: getAuthHeader() }))
      .data,

  // ---------- JOB SEEKERS ----------

  addJobSeeker: async (userData) =>
    (
      await api.post("/recruiter/add-job-seeker", userData, {
        headers: getAuthHeader(),
      })
    ).data,

  getMyJobSeekers: async () =>
    (await api.get("/recruiter/my-job-seekers", { headers: getAuthHeader() }))
      .data,

  getAllJobSeekers: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append("search", filters.search);
    if (filters.location) params.append("location", filters.location);
    if (filters.experience?.length)
      params.append("experience", filters.experience.join(","));
    if (filters.salary?.length)
      params.append("salary", filters.salary.join(","));
    if (filters.noticePeriod?.length)
      params.append("notice_period", filters.noticePeriod.join(","));
    if (filters.profileStatus?.length)
      params.append("profile_status", filters.profileStatus.join(","));

    return (
      await api.get(`/recruiter/all-job-seekers?${params.toString()}`, {
        headers: getAuthHeader(),
      })
    ).data;
  },

  getJobSeekerDetails: async (seekerId) =>
    (
      await api.get(`/recruiter/job-seeker/${seekerId}`, {
        headers: getAuthHeader(),
      })
    ).data,

  updateJobSeeker: async (seekerId, profileData) =>
    (
      await api.put(`/recruiter/job-seeker/${seekerId}`, profileData, {
        headers: getAuthHeader(),
      })
    ).data,

  // ---------- PIPELINE / APPLICATIONS ----------

  addToPipeline: async (jobId, seekerId) =>
    (
      await api.post(
        `/recruiter/add-to-pipeline?job_id=${jobId}&seeker_id=${seekerId}`,
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  assignToHR: async (appId) =>
    (
      await api.post(
        `/recruiter/application/${appId}/assign-hr`,
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  withdrawCandidate: async (appId) =>
    (
      await api.put(
        `/recruiter/withdraw-candidate/${appId}`,
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  reassignCandidate: async (appId) =>
    (
      await api.put(
        `/recruiter/reassign-candidate/${appId}`,
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  // ---------- ATS ----------

  getAllApplicants: async (stage = null) =>
    (
      await api.get("/recruiter/applicants", {
        headers: getAuthHeader(),
        params: stage ? { stage } : {},
      })
    ).data,

  getJobApplicants: async (jobId, stage = null) =>
    (
      await api.get(`/recruiter/job/${jobId}/applicants`, {
        headers: getAuthHeader(),
        params: stage ? { stage } : {},
      })
    ).data,

  getApplicationDetails: async (appId) =>
    (
      await api.get(`/recruiter/application/${appId}`, {
        headers: getAuthHeader(),
      })
    ).data,

  updateApplicationStatus: async (appId, stage, notes = "") =>
    (
      await api.put(
        `/recruiter/application/${appId}/status`,
        { stage, notes },
        { headers: getAuthHeader() },
      )
    ).data,

  scheduleInterview: async (appId, scheduleData) =>
    (
      await api.post(`/recruiter/application/${appId}/schedule`, scheduleData, {
        headers: getAuthHeader(),
      })
    ).data,

  submitFeedback: async (appId, feedbackData) =>
    (
      await api.post(`/recruiter/application/${appId}/feedback`, feedbackData, {
        headers: getAuthHeader(),
      })
    ).data,

  makeOffer: async (appId, offerData) =>
    (
      await api.post(`/recruiter/application/${appId}/offer`, offerData, {
        headers: getAuthHeader(),
      })
    ).data,

  hireCandidate: async (appId) =>
    (
      await api.put(
        `/recruiter/application/${appId}/hire`,
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  rejectCandidate: async (appId, notes = "") =>
    (
      await api.put(
        `/recruiter/application/${appId}/reject`,
        { stage: "rejected", notes },
        { headers: getAuthHeader() },
      )
    ).data,

  // ---------- NOTIFICATIONS ----------

  getNotifications: async (limit = 20, skip = 0) =>
    (
      await api.get(`/recruiter/notifications?limit=${limit}&skip=${skip}`, {
        headers: getAuthHeader(),
      })
    ).data,

  markNotificationRead: async (notifId) =>
    (
      await api.put(
        `/recruiter/notifications/${notifId}/read`,
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  markAllNotificationsRead: async () =>
    (
      await api.put(
        "/recruiter/notifications/mark-all-read",
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  // ---------- DOWNLOAD TRACKING ----------

  getDownloadLimit: async () =>
    (await api.get("/recruiter/download-limit", { headers: getAuthHeader() }))
      .data,

  logDownload: async (downloadData) =>
    (
      await api.post("/recruiter/log-download", downloadData, {
        headers: getAuthHeader(),
      })
    ).data,

  // ---------- DASHBOARD ----------

  getStats: async () =>
    (await api.get("/recruiter/stats", { headers: getAuthHeader() })).data,
};

// ===============================
// JOB SEEKER APPLICATIONS API
// ===============================
export const applicationsAPI = {
  applyToJob: async (jobId, coverLetter = "") =>
    (
      await api.post(
        `/jobs/${jobId}/apply`,
        { jobId, coverLetter },
        { headers: getAuthHeader() },
      )
    ).data,

  getMyApplications: async () =>
    (await api.get("/my-applications", { headers: getAuthHeader() })).data,
};

// ===============================
// PUBLIC JOB POSTINGS API
// ===============================
export const jobPostingsAPI = {
  getAll: async (params = {}) =>
    (await api.get("/job-postings", { params })).data,

  getById: async (id) => (await api.get(`/job-postings/${id}`)).data,
};

// ===================================
// COMPANY HR API
// ===================================

export const hrAPI = {
  // ===============================
  // Dashboard Data
  // ===============================
  getDashboard: async () =>
    (
      await api.get("/hr/dashboard", {
        headers: getAuthHeader(),
      })
    ).data,

  // ===============================
  // Get HR Jobs
  // ===============================
  getJobs: async () =>
    (
      await api.get("/hr/jobs", {
        headers: getAuthHeader(),
      })
    ).data,

  // ===============================
  // Get Applications (Stage + Pagination)
  // ===============================
  getJobApplications: async (jobId, stage = null, limit = 50, skip = 0) =>
    (
      await api.get(`/hr/job/${jobId}/applications`, {
        headers: getAuthHeader(),
        params: {
          ...(stage && { stage }),
          limit,
          skip,
        },
      })
    ).data,

  // ===============================
  // Get Single Application Details
  // ===============================
  getApplicationDetails: async (appId) =>
    (
      await api.get(`/hr/application/${appId}`, {
        headers: getAuthHeader(),
      })
    ).data,

  // ===============================
  // Update Application Status
  // ===============================
  updateApplicationStatus: async (appId, stage, notes = "") =>
    (
      await api.put(
        `/hr/application/${appId}/status`,
        { stage, notes },
        { headers: getAuthHeader() },
      )
    ).data,

  // ===============================
  // Schedule Interview
  // ===============================
  scheduleInterview: async (appId, scheduleData) =>
    (
      await api.post(
        `/hr/application/${appId}/schedule-interview`,
        scheduleData,
        { headers: getAuthHeader() },
      )
    ).data,

  // ===============================
  // Submit Feedback
  // ===============================
  submitFeedback: async (appId, feedbackData) =>
    (
      await api.post(`/hr/application/${appId}/feedback`, feedbackData, {
        headers: getAuthHeader(),
      })
    ).data,

  // ===============================
  // Make Offer
  // ===============================
  makeOffer: async (appId, offerData) =>
    (
      await api.post(`/hr/application/${appId}/offer`, offerData, {
        headers: getAuthHeader(),
      })
    ).data,

  // ===============================
  // Hire Candidate
  // ===============================
  hireCandidate: async (appId) =>
    (
      await api.put(
        `/hr/application/${appId}/hire`,
        {},
        { headers: getAuthHeader() },
      )
    ).data,

  // ===============================
  // Reject Candidate
  // ===============================
  rejectCandidate: async (appId, notes = "") =>
    (
      await api.put(
        `/hr/application/${appId}/reject`,
        { notes },
        { headers: getAuthHeader() },
      )
    ).data,

  // ===============================
  // Add Recruiter Note
  // ===============================
  addNote: async (appId, noteData) =>
    (
      await api.post(`/hr/application/${appId}/note`, noteData, {
        headers: getAuthHeader(),
      })
    ).data,

  // ===============================
  // Add Interview Feedback
  // ===============================
  addFeedback: async (appId, feedbackData) =>
    (
      await api.post(`/hr/application/${appId}/feedback`, feedbackData, {
        headers: getAuthHeader(),
      })
    ).data,
};

export const masterAPI = {
  getAll: () => api.get("/master/all"),
};

// ===============================
// EXPORT AXIOS INSTANCE
// ===============================
export default api;
